import { useMemo } from "react"
import PropTypes from "prop-types"
import { capitalize, map, chain, keys, find, keyBy, get, first, split } from "lodash"
import { Paper } from "@mui/material"
import { ROLE, PERMISSION_LEVEL } from "helpers/shapes"
import { APPLICATION } from "./shapes"
import classNames from "classnames"
import UpsellTabs, { UPSELL_TABS_SHAPE } from "components/material/upsell-tabs"
import Table from "components/material/table"
import { PERMISSION_LEVELS, PERMISSION_LEVELS_AS_OPTIONS, hasPermission } from "helpers/permissions"
import { formatLongDate } from "helpers/formatters/dates"

export default function ResourceApplications({ account, resourceId, resourceType, permissionLevel, role, resourceApplications, accountApplications, addNotification, update, tabs }) {
  const isAdmin = role === "admin"
  const hasWriteAccess = useMemo(() => isAdmin || hasPermission(permissionLevel, PERMISSION_LEVELS.OWNER), [isAdmin, permissionLevel])

  const applicationsById = useMemo(() => keyBy(accountApplications, "id"), [accountApplications])

  const availableOptionLabels = chain(accountApplications)
    .filter(({ id }) => !find(resourceApplications, a => a.id === id))
    .keyBy("id")
    .mapValues(p => p.name)
    .value()

  const addForm = useMemo(() => {
    if (!hasWriteAccess) {
      return null
    }

    function onAdd(data, cb) {
      const query = `mutation ($resourceId: IdOrShortIdOrAlias!, $resourceType: ResourceType!, $permissionLevel: PermissionLevel!) {
        ${map(data.applications, (applicationId, index) => `applications${index}: updatePermissions(resourceType: $resourceType, resourceId: $resourceId, permissions: { applicationId: "${applicationId}", permissionLevel: $permissionLevel }) { id }`)}
      }`

      const variables = { permissionLevel: data.permissionLevel, resourceId, resourceType }

      update(query, variables, account, {
        reloadData: ["resourceApplications", "applicationPermissions"],
      }, (errors, result) => {
        const permissionMessage = get(result, "apiMessages.messages.0.message")
        if (permissionMessage && permissionMessage.match(/limit of \d+ has been reached/i)) {
          addNotification("warning", first(split(permissionMessage, ":")))
        }
        cb(errors, result)
      })
    }

    return {
      title: "Add Applications",
      submitText: "Add Application",
      track: {
        type: "resource-permissions-add-application",
        payload: ({ applications, permissionLevel }) => ({
          applications: map(applications, id => get(applicationsById, [id, "name"], id)),
          permissionLevel,
        }),
      },
      onSubmit: onAdd,
      fields:
      [{
        id: "applications",
        label: "Applications",
        type: "multi",
        options: keys(availableOptionLabels),
        optionLabels: availableOptionLabels,
        validations: {
          isRequired: true,
        },
      }, {
        id: "permissionLevel",
        label: "Permission Level",
        type: "select",
        options: keys(PERMISSION_LEVELS),
        optionLabels: PERMISSION_LEVELS_AS_OPTIONS,
        validations: {
          isRequired: true,
        },
      }],
    }
  }, [hasWriteAccess, availableOptionLabels, resourceId, resourceType, update, account, addNotification, applicationsById])

  const editForm = useMemo(() => {
    function onEdit(data, cb) {
      const query = `mutation ($resourceId: IdOrShortIdOrAlias!, $resourceType: ResourceType!, $permissionLevel: PermissionLevel!) {
        updatePermissions(resourceType: $resourceType, resourceId: $resourceId, permissions: { applicationId: "${data.id}", permissionLevel: $permissionLevel }) { id }
      }`

      const variables = {
        resourceId,
        resourceType,
        permissionLevel: data.permissionLevel,
      }

      update(query, variables, account, {
        reloadData: ["resourceApplications", "applicationPermissions"],
      }, cb)
    }

    return {
      title: "Edit Permission Level",
      submitText: "Save",
      track: {
        type: "resource-permissions-edit-application",
        payload: ({ id, permissionLevel }) => ({
          resource: get(applicationsById, [id, "name"], id),
          permissionLevel,
        }),
      },
      isEnabled: () => hasWriteAccess,
      onSubmit: onEdit,
      getInitialValues: ({ permissionLevel }) => ({ permissionLevel }),
      fields: [{
        id: "permissionLevel",
        label: "Permission Level",
        type: "select",
        options: keys(PERMISSION_LEVELS),
        optionLabels: PERMISSION_LEVELS_AS_OPTIONS,
        validations: {
          isRequired: true,
        },
      }],
    }
  }, [resourceId, resourceType, update, account, hasWriteAccess, applicationsById])

  const deleteForm = useMemo(() => {
    function onDelete(data, cb) {
      const query = `mutation ($resourceId: IdOrShortIdOrAlias!, $resourceType: ResourceType!) {
        ${map(data, (application, index) => `application${index}: updatePermissions(resourceType: $resourceType, resourceId: $resourceId, permissions: { applicationId: "${application.id}", permissionLevel: NONE }) { id }`)}
      }`

      const variables = { resourceId, resourceType }

      update(query, variables, account, {
        reloadData: ["resourceApplications", "applicationPermissions"],
      }, cb)
    }

    return {
      type: "permission",
      track: {
        type: "resource-permissions-remove-application",
        payload: ({ name }) => ({ resource: name }),
      },
      isEnabled: () => hasWriteAccess,
      renderItem: resource => <li key={resource.id}>{resource.name}</li>,
      onSubmit: onDelete,
    }
  }, [account, hasWriteAccess, resourceId, resourceType, update])

  return <div className={classNames("modules_resource-applications", "with-header")}>
    <UpsellTabs id="resource-applications" tabs={tabs} />
    <Paper className="content" square>
      <Table
        title="Resource Permissions"
        idField="id"
        addForm={addForm}
        editForm={editForm}
        deleteForm={deleteForm}
        data={resourceApplications}
        columns={[{
          name: "Name",
          id: "name",
        }, {
          name: "Description",
          id: "description",
        }, {
          name: "Created",
          id: "createdAt",
          formatter: formatLongDate,
        }, {
          name: "Permission Level",
          id: "permissionLevel",
          formatter: capitalize,
        }]}
      />
    </Paper>
  </div>
}

ResourceApplications.propTypes = {
  account: PropTypes.string.isRequired,
  role: ROLE.isRequired,
  permissionLevel: PERMISSION_LEVEL,
  resourceId: PropTypes.string,
  resourceAlias: PropTypes.string.isRequired,
  resourceType: PropTypes.string,
  resourceApplications: PropTypes.arrayOf(APPLICATION).isRequired,
  accountApplications: PropTypes.arrayOf(APPLICATION).isRequired,
  update: PropTypes.func.isRequired,
  addNotification: PropTypes.func.isRequired,
  tabs: UPSELL_TABS_SHAPE,
}
